import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  LayoutDashboard,
  FileText,
  Scale,
  Bell,
  LogOut,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Building2,
  FolderOpen,
  Upload,
  Trash2,
  Pencil,
  BarChart3,
  FileCheck,
  Users,
  Mail,
  ChevronDown,
  ChevronUp,
  Paperclip,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// اللون الموحد لجميع الشركات (وردي غامق)
const THEME_COLOR = '#E94B7B';
const THEME_COLOR_HOVER = '#d43d6a';

// Check if company is Awtad Al-Omran
const isAwtadCompany = (companyName) => {
  return companyName && companyName.includes('أوتاد');
};

// Check if company should have Letters (IFG or Awtad)
const hasLettersModule = (companyName) => {
  return companyName && (companyName.includes('IFG') || companyName.includes('القابضة') || companyName.includes('أوتاد'));
};

// Check if company is IFG
const isIFGCompany = (companyName) => {
  return companyName && (companyName.includes('IFG') || companyName.includes('القابضة'));
};

// Check if company is وسيط الكل
const isWaseetCompany = (companyName) => {
  return companyName && companyName.includes('وسيط');
};

// Check if company should have Agreements (IFG, وسيط, أوتاد)
const hasAgreementsModule = (companyName) => {
  return companyName && (
    companyName.includes('IFG') || 
    companyName.includes('القابضة') || 
    companyName.includes('وسيط') ||
    companyName.includes('أوتاد')
  );
};

export default function CompanyDashboard({ user }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'contracts';
  const [company, setCompany] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [contracts, setContracts] = useState([]);
  const [cases, setCases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [commitments, setCommitments] = useState([]);
  const [letters, setLetters] = useState([]);
  const [annexes, setAnnexes] = useState({});
  const [expandedAgreements, setExpandedAgreements] = useState({});
  const [stats, setStats] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [letterTypeFilter, setLetterTypeFilter] = useState('all');
  const [isAddContractDialogOpen, setIsAddContractDialogOpen] = useState(false);
  const [isAddCaseDialogOpen, setIsAddCaseDialogOpen] = useState(false);
  const [isAddProjectDialogOpen, setIsAddProjectDialogOpen] = useState(false);
  const [isEditProjectDialogOpen, setIsEditProjectDialogOpen] = useState(false);
  const [isAddAgreementDialogOpen, setIsAddAgreementDialogOpen] = useState(false);
  const [isAddCommitmentDialogOpen, setIsAddCommitmentDialogOpen] = useState(false);
  const [isAddLetterDialogOpen, setIsAddLetterDialogOpen] = useState(false);
  const [isAddAnnexDialogOpen, setIsAddAnnexDialogOpen] = useState(false);
  const [selectedAgreementForAnnex, setSelectedAgreementForAnnex] = useState(null);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', id: '', title: '' });
  const [loading, setLoading] = useState(true);
  const [uploadingDeed, setUploadingDeed] = useState(null);
  const [editingProject, setEditingProject] = useState(null);

  const [newContract, setNewContract] = useState({
    title: '',
    description: '',
    contract_number: '',
    party_name: '',
    contract_value: '',
    notes: '',
    project_id: '',
  });

  const [newCase, setNewCase] = useState({
    case_number: '',
    title: '',
    description: '',
    case_type: '',
    plaintiff: '',
    defendant: '',
    court_name: '',
    notes: '',
  });

  const [newProject, setNewProject] = useState({
    name: '',
    area: '',
    deedFile: null,
  });

  const [newAgreement, setNewAgreement] = useState({
    agreement_number: '',
    title: '',
    description: '',
    party_name: '',
    agreement_date: '',
    notes: '',
    file: null,
  });

  const [newCommitment, setNewCommitment] = useState({
    commitment_number: '',
    employee_name: '',
    employee_id: '',
    commitment_type: '',
    description: '',
    commitment_date: '',
    notes: '',
    file: null,
  });

  const [newLetter, setNewLetter] = useState({
    letter_number: '',
    letter_date: '',
    subject: '',
    sender: '',
    recipient: '',
    letter_type: 'outgoing',
    description: '',
    file: null,
  });

  const [newAnnex, setNewAnnex] = useState({
    annex_number: '',
    title: '',
    annex_date: '',
    description: '',
    file: null,
  });

  useEffect(() => {
    fetchCompany();
  }, [companyId]);

  useEffect(() => {
    if (company) {
      fetchData();
      if (isAwtadCompany(company.name_ar)) {
        fetchProjects();
      }
      if (hasAgreementsModule(company.name_ar)) {
        fetchAgreements();
      }
      if (isIFGCompany(company.name_ar)) {
        fetchCommitments();
      }
      if (hasLettersModule(company.name_ar)) {
        fetchLetters();
      }
    }
  }, [company, statusFilter, searchTerm, letterTypeFilter, activeTab]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      const foundCompany = response.data.find(c => c.id === companyId);
      if (foundCompany) {
        setCompany(foundCompany);
      } else {
        toast.error('الشركة غير موجودة');
        navigate('/companies');
      }
    } catch (error) {
      toast.error('فشل تحميل بيانات الشركة');
      navigate('/companies');
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, {
        params: { company_id: companyId },
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    }
  };

  const fetchAgreements = async () => {
    try {
      const response = await axios.get(`${API}/agreements`, {
        params: { company_id: companyId },
      });
      setAgreements(response.data);
    } catch (error) {
      console.error('Failed to fetch agreements');
    }
  };

  const fetchCommitments = async () => {
    try {
      const response = await axios.get(`${API}/commitments`, {
        params: { company_id: companyId },
      });
      setCommitments(response.data);
    } catch (error) {
      console.error('Failed to fetch commitments');
    }
  };

  const fetchLetters = async () => {
    try {
      const response = await axios.get(`${API}/letters`, {
        params: { 
          company_id: companyId,
          letter_type: letterTypeFilter !== 'all' ? letterTypeFilter : undefined,
          search: searchTerm || undefined,
        },
      });
      setLetters(response.data);
    } catch (error) {
      console.error('Failed to fetch letters');
    }
  };

  const fetchAnnexes = async (agreementId) => {
    try {
      const response = await axios.get(`${API}/agreements/${agreementId}/annexes`);
      setAnnexes(prev => ({ ...prev, [agreementId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch annexes');
    }
  };

  const toggleAgreementExpand = (agreementId) => {
    setExpandedAgreements(prev => {
      const isExpanded = !prev[agreementId];
      if (isExpanded && !annexes[agreementId]) {
        fetchAnnexes(agreementId);
      }
      return { ...prev, [agreementId]: isExpanded };
    });
  };

  const fetchData = async () => {
    try {
      const [statsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/stats`, {
          params: { company_id: companyId },
        }),
        axios.get(`${API}/notifications`, {
          params: { company_id: companyId },
        }),
      ]);

      setStats(statsRes.data);
      setNotifications(notificationsRes.data);

      if (activeTab === 'contracts') {
        const contractsRes = await axios.get(`${API}/contracts`, {
          params: {
            company_id: companyId,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setContracts(contractsRes.data);
      } else if (activeTab === 'cases') {
        const casesRes = await axios.get(`${API}/cases`, {
          params: {
            company_id: companyId,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setCases(casesRes.data);
      } else if (activeTab === 'agreements') {
        const agreementsRes = await axios.get(`${API}/agreements`, {
          params: {
            company_id: companyId,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setAgreements(agreementsRes.data);
      } else if (activeTab === 'commitments') {
        const commitmentsRes = await axios.get(`${API}/commitments`, {
          params: {
            company_id: companyId,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchTerm || undefined,
          },
        });
        setCommitments(commitmentsRes.data);
      } else if (activeTab === 'projects') {
        await fetchProjects();
      }
    } catch (error) {
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contracts`, {
        ...newContract,
        company_id: companyId,
        contract_value: newContract.contract_value ? parseFloat(newContract.contract_value) : null,
        project_id: newContract.project_id || null,
      });
      toast.success('تم إضافة العقد بنجاح');
      setIsAddContractDialogOpen(false);
      setNewContract({
        title: '',
        description: '',
        contract_number: '',
        party_name: '',
        contract_value: '',
        notes: '',
        project_id: '',
      });
      fetchData();
    } catch (error) {
      toast.error('فشل إضافة العقد');
    }
  };

  const handleCreateCase = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/cases`, {
        ...newCase,
        company_id: companyId,
      });
      toast.success('تم إضافة القضية بنجاح');
      setIsAddCaseDialogOpen(false);
      setNewCase({
        case_number: '',
        title: '',
        description: '',
        case_type: '',
        plaintiff: '',
        defendant: '',
        court_name: '',
        notes: '',
      });
      fetchData();
    } catch (error) {
      toast.error('فشل إضافة القضية');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      // Create project first
      const projectRes = await axios.post(`${API}/projects`, {
        name: newProject.name,
        area: newProject.area,
        company_id: companyId,
      });
      
      // Upload deed if file is selected
      if (newProject.deedFile) {
        const formData = new FormData();
        formData.append('file', newProject.deedFile);
        await axios.post(`${API}/projects/${projectRes.data.id}/upload-deed`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم إضافة المشروع بنجاح');
      setIsAddProjectDialogOpen(false);
      setNewProject({
        name: '',
        area: '',
        deedFile: null,
      });
      fetchProjects();
      if (activeTab === 'projects') {
        fetchData();
      }
    } catch (error) {
      toast.error('فشل إضافة المشروع');
    }
  };

  const handleUploadDeed = async (projectId, file) => {
    if (!file) return;
    
    setUploadingDeed(projectId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/projects/${projectId}/upload-deed`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع صك الأرض بنجاح');
      fetchProjects();
    } catch (error) {
      toast.error('فشل رفع صك الأرض');
    } finally {
      setUploadingDeed(null);
    }
  };

  const handleEditProject = (project) => {
    setEditingProject({
      id: project.id,
      name: project.name,
      area: project.area || '',
      deed_file_name: project.deed_file_name || null,
      newDeedFile: null,
    });
    setIsEditProjectDialogOpen(true);
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    try {
      // Update project data
      await axios.put(`${API}/projects/${editingProject.id}`, {
        name: editingProject.name,
        area: editingProject.area,
      });
      
      // Upload new deed if selected
      if (editingProject.newDeedFile) {
        const formData = new FormData();
        formData.append('file', editingProject.newDeedFile);
        await axios.post(`${API}/projects/${editingProject.id}/upload-deed`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم تحديث المشروع بنجاح');
      setIsEditProjectDialogOpen(false);
      setEditingProject(null);
      fetchProjects();
    } catch (error) {
      toast.error('فشل تحديث المشروع');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المشروع؟')) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success('تم حذف المشروع بنجاح');
      fetchProjects();
    } catch (error) {
      toast.error('فشل حذف المشروع');
    }
  };

  // Agreement handlers
  const handleCreateAgreement = async (e) => {
    e.preventDefault();
    try {
      // Create agreement first
      const response = await axios.post(`${API}/agreements`, {
        agreement_number: newAgreement.agreement_number,
        title: newAgreement.title,
        description: newAgreement.description,
        party_name: newAgreement.party_name,
        notes: newAgreement.notes,
        company_id: companyId,
        agreement_date: newAgreement.agreement_date ? new Date(newAgreement.agreement_date).toISOString() : null,
      });
      
      // Upload file if selected
      if (newAgreement.file && response.data.id) {
        const formData = new FormData();
        formData.append('file', newAgreement.file);
        await axios.post(`${API}/agreements/${response.data.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم إضافة الاتفاقية بنجاح');
      setIsAddAgreementDialogOpen(false);
      setNewAgreement({
        agreement_number: '',
        title: '',
        description: '',
        party_name: '',
        agreement_date: '',
        notes: '',
        file: null,
      });
      fetchAgreements();
    } catch (error) {
      toast.error('فشل إضافة الاتفاقية');
    }
  };

  const handleDeleteAgreement = async (agreementId) => {
    try {
      await axios.delete(`${API}/agreements/${agreementId}`);
      toast.success('تم حذف الاتفاقية بنجاح');
      setIsDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', title: '' });
      fetchAgreements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف الاتفاقية');
    }
  };

  // Commitment handlers
  const handleCreateCommitment = async (e) => {
    e.preventDefault();
    try {
      // Create commitment first
      const response = await axios.post(`${API}/commitments`, {
        commitment_number: newCommitment.commitment_number,
        employee_name: newCommitment.employee_name,
        employee_id: newCommitment.employee_id,
        commitment_type: newCommitment.commitment_type,
        description: newCommitment.description,
        notes: newCommitment.notes,
        company_id: companyId,
        commitment_date: newCommitment.commitment_date ? new Date(newCommitment.commitment_date).toISOString() : null,
      });
      
      // Upload file if selected
      if (newCommitment.file && response.data.id) {
        const formData = new FormData();
        formData.append('file', newCommitment.file);
        await axios.post(`${API}/commitments/${response.data.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم إضافة الإقرار بنجاح');
      setIsAddCommitmentDialogOpen(false);
      setNewCommitment({
        commitment_number: '',
        employee_name: '',
        employee_id: '',
        commitment_type: '',
        description: '',
        commitment_date: '',
        notes: '',
        file: null,
      });
      fetchCommitments();
    } catch (error) {
      toast.error('فشل إضافة الإقرار');
    }
  };

  const handleDeleteCommitment = async (commitmentId) => {
    try {
      await axios.delete(`${API}/commitments/${commitmentId}`);
      toast.success('تم حذف الإقرار بنجاح');
      setIsDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', title: '' });
      fetchCommitments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف الإقرار');
    }
  };

  // إنشاء خطاب جديد
  const handleCreateLetter = async (e) => {
    e.preventDefault();
    try {
      const letterData = {
        company_id: companyId,
        letter_number: newLetter.letter_number,
        letter_date: newLetter.letter_date || null,
        subject: newLetter.subject,
        sender: newLetter.sender || null,
        recipient: newLetter.recipient || null,
        letter_type: newLetter.letter_type,
        description: newLetter.description || null,
      };
      
      const response = await axios.post(`${API}/letters`, letterData);
      
      // رفع الملف إذا وجد
      if (newLetter.file) {
        const formData = new FormData();
        formData.append('file', newLetter.file);
        await axios.post(`${API}/letters/${response.data.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم إضافة الخطاب بنجاح');
      setIsAddLetterDialogOpen(false);
      setNewLetter({
        letter_number: '',
        letter_date: '',
        subject: '',
        sender: '',
        recipient: '',
        letter_type: 'outgoing',
        description: '',
        file: null,
      });
      fetchLetters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة الخطاب');
    }
  };

  // حذف خطاب
  const handleDeleteLetter = async (letterId) => {
    try {
      await axios.delete(`${API}/letters/${letterId}`);
      toast.success('تم حذف الخطاب بنجاح');
      setIsDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', title: '' });
      fetchLetters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف الخطاب');
    }
  };

  // إنشاء ملحق جديد
  const handleCreateAnnex = async (e) => {
    e.preventDefault();
    try {
      const annexData = {
        annex_number: newAnnex.annex_number,
        title: newAnnex.title,
        annex_date: newAnnex.annex_date || null,
        description: newAnnex.description || null,
      };
      
      const response = await axios.post(`${API}/agreements/${selectedAgreementForAnnex}/annexes`, annexData);
      
      // رفع الملف إذا وجد
      if (newAnnex.file) {
        const formData = new FormData();
        formData.append('file', newAnnex.file);
        await axios.post(`${API}/annexes/${response.data.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      toast.success('تم إضافة الملحق بنجاح');
      setIsAddAnnexDialogOpen(false);
      setNewAnnex({
        annex_number: '',
        title: '',
        annex_date: '',
        description: '',
        file: null,
      });
      fetchAnnexes(selectedAgreementForAnnex);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل إضافة الملحق');
    }
  };

  // حذف ملحق
  const handleDeleteAnnex = async (annexId, agreementId) => {
    try {
      await axios.delete(`${API}/annexes/${annexId}`);
      toast.success('تم حذف الملحق بنجاح');
      fetchAnnexes(agreementId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف الملحق');
    }
  };

  // رفع ملف للملحق
  const handleUploadAnnexFile = async (annexId, file, agreementId) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/annexes/${annexId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع الملف بنجاح');
      fetchAnnexes(agreementId);
    } catch (error) {
      toast.error('فشل رفع الملف');
    }
  };

  // رفع ملف للخطاب
  const handleUploadLetterFile = async (letterId, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/letters/${letterId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع الملف بنجاح');
      fetchLetters();
    } catch (error) {
      toast.error('فشل رفع الملف');
    }
  };

  const handleStatusChange = async (id, newStatus, type) => {
    try {
      const endpoint = type === 'contract' ? 'contracts' : 'cases';
      await axios.patch(`${API}/${endpoint}/${id}`, { status: newStatus });
      toast.success(`تم تحديث حالة ${type === 'contract' ? 'العقد' : 'القضية'} بنجاح`);
      fetchData();
    } catch (error) {
      toast.error(`فشل تحديث حالة ${type === 'contract' ? 'العقد' : 'القضية'}`);
    }
  };

  const handleApproveContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/approve`);
      toast.success('تم اعتماد العقد بنجاح');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل اعتماد العقد');
    }
  };

  // تغيير حالة الاتفاقية
  const handleAgreementStatusChange = async (agreementId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/agreements/${agreementId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const statusMessages = {
        review: 'تم إرسال الاتفاقية للمراجعة',
        approved: 'تم اعتماد الاتفاقية',
        signed: 'تم تفعيل الاتفاقية',
      };
      toast.success(statusMessages[newStatus] || 'تم تحديث الحالة');
      fetchAgreements();
    } catch (error) {
      toast.error('فشل تحديث حالة الاتفاقية');
    }
  };

  // تغيير حالة الإقرار
  const handleCommitmentStatusChange = async (commitmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/commitments/${commitmentId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const statusMessages = {
        review: 'تم إرسال الإقرار للمراجعة',
        approved: 'تم اعتماد الإقرار',
        signed: 'تم توقيع الإقرار',
      };
      toast.success(statusMessages[newStatus] || 'تم تحديث الحالة');
      fetchCommitments();
    } catch (error) {
      toast.error('فشل تحديث حالة الإقرار');
    }
  };

  // تحديث ملف الاتفاقية
  const handleUpdateAgreementFile = async (agreementId, file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/agreements/${agreementId}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      toast.success('تم تحديث الملف بنجاح');
      fetchAgreements();
    } catch (error) {
      toast.error('فشل تحديث الملف');
    }
  };

  // تحديث ملف الإقرار
  const handleUpdateCommitmentFile = async (commitmentId, file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/commitments/${commitmentId}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      toast.success('تم تحديث الملف بنجاح');
      fetchCommitments();
    } catch (error) {
      toast.error('فشل تحديث الملف');
    }
  };

  // تحديث ملف العقد
  const handleUpdateContractFile = async (contractId, file) => {
    if (!file) return;
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/contracts/${contractId}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      toast.success('تم تحديث الملف بنجاح');
      fetchData();
    } catch (error) {
      toast.error('فشل تحديث الملف');
    }
  };

  const markNotificationAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`);
      fetchData();
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('تم تسجيل الخروج بنجاح');
  };

  const handleDeleteContract = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف العقد بنجاح');
      setIsDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', title: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف العقد');
    }
  };

  const handleDeleteCase = async (caseId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/cases/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('تم حذف القضية بنجاح');
      setIsDeleteDialogOpen(false);
      setDeleteTarget({ type: '', id: '', title: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل حذف القضية');
    }
  };

  const openDeleteDialog = (type, id, title) => {
    setDeleteTarget({ type, id, title });
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deleteTarget.type === 'contract') {
      handleDeleteContract(deleteTarget.id);
    } else if (deleteTarget.type === 'case') {
      handleDeleteCase(deleteTarget.id);
    } else if (deleteTarget.type === 'agreement') {
      handleDeleteAgreement(deleteTarget.id);
    } else if (deleteTarget.type === 'commitment') {
      handleDeleteCommitment(deleteTarget.id);
    } else if (deleteTarget.type === 'letter') {
      handleDeleteLetter(deleteTarget.id);
    }
  };

  const getDeleteTypeLabel = (type) => {
    const labels = {
      contract: 'العقد',
      case: 'القضية',
      agreement: 'الاتفاقية',
      commitment: 'الإقرار',
      letter: 'الخطاب',
    };
    return labels[type] || '';
  };

  const getContractStatusBadge = (status) => {
    const statusMap = {
      draft: { text: 'مسودة', class: 'status-draft' },
      review: { text: 'قيد المراجعة', class: 'status-review' },
      approved: { text: 'معتمد', class: 'status-approved' },
      signed: { text: 'موقّع', class: 'status-signed' },
    };
    const statusInfo = statusMap[status] || statusMap.draft;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const getCaseStatusBadge = (status) => {
    const statusMap = {
      new: { text: 'جديدة', class: 'status-new' },
      filed: { text: 'مرفوعة', class: 'status-filed' },
      hearings: { text: 'جلسات', class: 'status-hearings' },
      final_judgment: { text: 'حكم نهائي', class: 'status-final_judgment' },
      execution: { text: 'تنفيذ', class: 'status-execution' },
    };
    const statusInfo = statusMap[status] || statusMap.new;
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const showProjects = isAwtadCompany(company?.name_ar);

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      <div className="flex">
        {/* Sidebar */}
        <aside className="w-72 bg-gradient-to-b from-slate-800 to-slate-900 min-h-screen p-6 fixed right-0 top-0">
          {/* Company Logo */}
          <div className="bg-white rounded-xl p-4 mb-6">
            {company.logo_url ? (
              <img
                src={company.logo_url}
                alt={company.name_ar}
                className="w-full h-20 object-contain"
              />
            ) : (
              <Building2 size={48} className="mx-auto text-slate-400" />
            )}
            <h2 className="text-slate-800 font-bold text-center mt-2 text-sm">{company.name_ar}</h2>
          </div>
          
          {/* Navigation */}
          <nav className="space-y-2">
            <button
              onClick={() => navigate('/companies')}
              className="sidebar-item w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              data-testid="back-to-companies"
            >
              <ArrowRight size={18} />
              <span>العودة للشركات</span>
            </button>
            
            <div
              className="sidebar-item w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-white"
              style={{ backgroundColor: THEME_COLOR }}
            >
              <LayoutDashboard size={18} />
              <span>لوحة التحكم</span>
            </div>
            
            <button
              className="sidebar-item w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors relative"
              onClick={() => setIsNotificationsOpen(true)}
              data-testid="notifications-nav-item"
            >
              <Bell size={18} />
              <span>الإشعارات</span>
              {unreadCount > 0 && (
                <span 
                  className="absolute left-4 top-2 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
                  style={{ backgroundColor: THEME_COLOR }}
                >
                  {unreadCount}
                </span>
              )}
            </button>
            
            {/* التقارير - للمدير القانوني فقط */}
            {user?.role === 'legal_manager' && (
              <button
                onClick={() => navigate(`/reports/${companyId}`)}
                className="sidebar-item w-full text-right px-4 py-2.5 rounded-md flex items-center gap-3 text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <BarChart3 size={18} />
                <span>التقارير</span>
              </button>
            )}
          </nav>

          {/* User Info */}
          <div className="absolute bottom-6 right-6 left-6">
            <div className="border-t border-slate-700 pt-4">
              <p className="text-white font-medium">{user?.full_name}</p>
              <p className="text-slate-400 text-sm">
                مدير الشؤون القانونية
              </p>
              {user?.last_login && (
                <p className="text-slate-500 text-xs mt-1">
                  آخر دخول: {new Date(user.last_login).toLocaleDateString('ar-SA')} - {new Date(user.last_login).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700 mt-2"
                data-testid="logout-button"
              >
                <LogOut size={16} className="ml-2" />
                تسجيل الخروج
              </Button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 mr-72 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800">{company.name_ar}</h1>
            <p className="text-slate-500 mt-1">إدارة العقود والقضايا القانونية</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-6 mb-8">
            <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-blue-600">{stats.contracts?.total || 0}</p>
                  <p className="text-slate-500 text-sm mt-1">إجمالي العقود</p>
                </div>
                <FileText className="text-blue-200" size={32} />
              </div>
            </div>
            <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-amber-600">{stats.contracts?.review || 0}</p>
                  <p className="text-slate-500 text-sm mt-1">قيد المراجعة</p>
                </div>
                <Clock className="text-amber-200" size={32} />
              </div>
            </div>
            <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold" style={{ color: THEME_COLOR }}>{stats.cases?.total || 0}</p>
                  <p className="text-slate-500 text-sm mt-1">إجمالي القضايا</p>
                </div>
                <Scale style={{ color: '#f9a8d4' }} size={32} />
              </div>
            </div>
            <div className="stats-card bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-3xl font-bold text-green-600">{stats.cases?.hearings || 0}</p>
                  <p className="text-slate-500 text-sm mt-1">جلسات</p>
                </div>
                <AlertCircle className="text-green-200" size={32} />
              </div>
            </div>
          </div>

          {/* Tabs */}
        <div className="border-b border-slate-200 mb-6">
          <div className="flex gap-8">
            <button
              onClick={() => {
                setActiveTab('contracts');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'contracts' ? 'active' : ''}`}
              data-testid="contracts-tab"
            >
              <FileText size={18} className="inline-block ml-2" />
              العقود
            </button>
            <button
              onClick={() => {
                setActiveTab('cases');
                setStatusFilter('all');
                setSearchTerm('');
              }}
              className={`tab-button ${activeTab === 'cases' ? 'active' : ''}`}
              data-testid="cases-tab"
            >
              <Scale size={18} className="inline-block ml-2" />
              القضايا
            </button>
            {/* تبويب الاتفاقيات - IFG و وسيط الكل و أوتاد */}
            {hasAgreementsModule(company?.name_ar) && (
              <button
                onClick={() => {
                  setActiveTab('agreements');
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
                className={`tab-button ${activeTab === 'agreements' ? 'active' : ''}`}
                data-testid="agreements-tab"
              >
                <FileCheck size={18} className="inline-block ml-2" />
                الاتفاقيات
              </button>
            )}
            {/* تبويب إقرارات الموظفين - IFG فقط */}
            {isIFGCompany(company?.name_ar) && (
              <button
                onClick={() => {
                  setActiveTab('commitments');
                  setStatusFilter('all');
                  setSearchTerm('');
                }}
                className={`tab-button ${activeTab === 'commitments' ? 'active' : ''}`}
                data-testid="commitments-tab"
              >
                <Users size={18} className="inline-block ml-2" />
                إقرارات الموظفين
              </button>
            )}
            {showProjects && (
              <button
                onClick={() => {
                  setActiveTab('projects');
                }}
                className={`tab-button ${activeTab === 'projects' ? 'active' : ''}`}
                data-testid="projects-tab"
              >
                <FolderOpen size={18} className="inline-block ml-2" />
                المشاريع
              </button>
            )}
            {/* تبويب الخطابات - IFG و أوتاد العمران */}
            {hasLettersModule(company?.name_ar) && (
              <button
                onClick={() => {
                  setActiveTab('letters');
                  setLetterTypeFilter('all');
                  setSearchTerm('');
                }}
                className={`tab-button ${activeTab === 'letters' ? 'active' : ''}`}
                data-testid="letters-tab"
              >
                <Mail size={18} className="inline-block ml-2" />
                الخطابات
              </button>
            )}
          </div>
        </div>

        {/* Projects Tab Content */}
        {activeTab === 'projects' && showProjects && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-slate-800">قائمة المشاريع</h3>
              <Dialog open={isAddProjectDialogOpen} onOpenChange={setIsAddProjectDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="text-white h-11 px-6 shadow-md"
                    style={{ backgroundColor: THEME_COLOR }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                    data-testid="add-project-button"
                  >
                    <Plus size={20} className="ml-2" />
                    إضافة مشروع جديد
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg" dir="rtl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-right">إضافة مشروع جديد</DialogTitle>
                    <DialogDescription className="text-right">
                      أدخل تفاصيل المشروع الجديد
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="project-name" className="text-right block">اسم المشروع *</Label>
                      <Input
                        id="project-name"
                        value={newProject.name}
                        onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                        required
                        className="text-right"
                        data-testid="project-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="project-area" className="text-right block">مساحة المشروع</Label>
                      <Input
                        id="project-area"
                        value={newProject.area}
                        onChange={(e) => setNewProject({ ...newProject, area: e.target.value })}
                        placeholder="مثال: 500 متر مربع"
                        className="text-right"
                        data-testid="project-area-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="project-deed" className="text-right block">صك الأرض</Label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer">
                          <Input
                            id="project-deed"
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => setNewProject({ ...newProject, deedFile: e.target.files[0] })}
                            data-testid="project-deed-input"
                          />
                          <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 transition-colors">
                            <Upload size={18} className="text-slate-500" />
                            <span className="text-sm text-slate-600">
                              {newProject.deedFile ? newProject.deedFile.name : 'اختر ملف الصك (PDF أو صورة)'}
                            </span>
                          </div>
                        </label>
                        {newProject.deedFile && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => setNewProject({ ...newProject, deedFile: null })}
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1 text-white" style={{ backgroundColor: THEME_COLOR }} data-testid="submit-project-button">
                        إضافة المشروع
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddProjectDialogOpen(false)}
                        className="flex-1"
                      >
                        إلغاء
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
              {projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FolderOpen size={48} className="mb-4" />
                  <p className="text-lg">لا توجد مشاريع</p>
                  <p className="text-sm mt-2">أضف مشروعاً جديداً للبدء</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">اسم المشروع</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المساحة</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">صك الأرض</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {projects.map((project) => (
                        <tr key={project.id} className="hover:bg-slate-50 transition-colors" data-testid={`project-row-${project.id}`}>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{project.name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{project.area || '-'}</td>
                          <td className="px-6 py-4">
                            {project.deed_file_name ? (
                              <a
                                href={`/api/files/project_deed/${project.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-sm"
                              >
                                {project.deed_file_name}
                              </a>
                            ) : (
                              <span className="text-sm text-slate-400">لا يوجد</span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {new Date(project.created_at).toLocaleDateString('ar-SA')}
                          </td>
                          <td className="px-6 py-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              onClick={() => handleEditProject(project)}
                              data-testid={`edit-project-${project.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteProject(project.id)}
                              data-testid={`delete-project-${project.id}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Edit Project Dialog */}
        <Dialog open={isEditProjectDialogOpen} onOpenChange={setIsEditProjectDialogOpen}>
          <DialogContent className="max-w-lg" dir="rtl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-right">تعديل المشروع</DialogTitle>
              <DialogDescription className="text-right">
                قم بتعديل بيانات المشروع
              </DialogDescription>
            </DialogHeader>
            {editingProject && (
              <form onSubmit={handleUpdateProject} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-project-name" className="text-right block">اسم المشروع *</Label>
                  <Input
                    id="edit-project-name"
                    value={editingProject.name}
                    onChange={(e) => setEditingProject({ ...editingProject, name: e.target.value })}
                    required
                    className="text-right"
                    data-testid="edit-project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-project-area" className="text-right block">مساحة المشروع</Label>
                  <Input
                    id="edit-project-area"
                    value={editingProject.area}
                    onChange={(e) => setEditingProject({ ...editingProject, area: e.target.value })}
                    placeholder="مثال: 500 متر مربع"
                    className="text-right"
                    data-testid="edit-project-area-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-project-deed" className="text-right block">صك الأرض</Label>
                  {editingProject.deed_file_name && !editingProject.newDeedFile && (
                    <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg mb-2">
                      <span className="text-sm text-slate-600 flex-1">الصك الحالي: {editingProject.deed_file_name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setEditingProject({ ...editingProject, deed_file_name: null })}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <label className="flex-1 cursor-pointer">
                      <Input
                        id="edit-project-deed"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="hidden"
                        onChange={(e) => setEditingProject({ ...editingProject, newDeedFile: e.target.files[0] })}
                        data-testid="edit-project-deed-input"
                      />
                      <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 transition-colors">
                        <Upload size={18} className="text-slate-500" />
                        <span className="text-sm text-slate-600">
                          {editingProject.newDeedFile ? editingProject.newDeedFile.name : 'رفع صك جديد (PDF أو صورة)'}
                        </span>
                      </div>
                    </label>
                    {editingProject.newDeedFile && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => setEditingProject({ ...editingProject, newDeedFile: null })}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 text-white" style={{ backgroundColor: THEME_COLOR }} data-testid="update-project-button">
                    حفظ التعديلات
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsEditProjectDialogOpen(false);
                      setEditingProject(null);
                    }}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Agreements Tab Content */}
        {activeTab === 'agreements' && hasAgreementsModule(company?.name_ar) && (
          <>
            {/* Search, Filter and Add Button Bar for Agreements - All in One Row */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder="البحث في الاتفاقيات..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 h-11 bg-white border-slate-200"
                  data-testid="agreements-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="agreements-filter-status-select">
                  <Filter size={16} className="ml-2" />
                  <SelectValue placeholder="تصفية حسب الحالة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="signed">موقّع</SelectItem>
                </SelectContent>
              </Select>
              {user?.role === 'legal_manager' && (
                <Dialog open={isAddAgreementDialogOpen} onOpenChange={setIsAddAgreementDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="text-white h-11 px-6 shadow-md"
                      style={{ backgroundColor: THEME_COLOR }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                      data-testid="add-agreement-button"
                    >
                      <Plus size={20} className="ml-2" />
                      إضافة اتفاقية جديدة
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]" dir="rtl">
                    <DialogHeader>
                      <DialogTitle className="text-right text-2xl">إضافة اتفاقية جديدة</DialogTitle>
                      <DialogDescription className="text-right">أدخل تفاصيل الاتفاقية</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateAgreement} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-right block">رقم الاتفاقية *</Label>
                          <Input
                            value={newAgreement.agreement_number}
                            onChange={(e) => setNewAgreement({ ...newAgreement, agreement_number: e.target.value })}
                            required
                            className="text-right"
                            placeholder="مثال: AGR-2024-001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-right block">تاريخ الاتفاقية</Label>
                          <Input
                            type="date"
                            value={newAgreement.agreement_date}
                            onChange={(e) => setNewAgreement({ ...newAgreement, agreement_date: e.target.value })}
                            className="text-left"
                            dir="ltr"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">عنوان الاتفاقية *</Label>
                        <Input
                          value={newAgreement.title}
                          onChange={(e) => setNewAgreement({ ...newAgreement, title: e.target.value })}
                          required
                          className="text-right"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">الطرف الثاني *</Label>
                        <Input
                          value={newAgreement.party_name}
                          onChange={(e) => setNewAgreement({ ...newAgreement, party_name: e.target.value })}
                          required
                          className="text-right"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">الوصف</Label>
                        <Textarea
                          value={newAgreement.description}
                          onChange={(e) => setNewAgreement({ ...newAgreement, description: e.target.value })}
                          className="text-right"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">ملف الاتفاقية (PDF)</Label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <Input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => setNewAgreement({ ...newAgreement, file: e.target.files[0] })}
                              data-testid="agreement-file-input"
                            />
                            <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 transition-colors">
                              <Upload size={18} className="text-slate-500" />
                              <span className="text-sm text-slate-600">
                                {newAgreement.file ? newAgreement.file.name : 'اختر ملف PDF'}
                              </span>
                            </div>
                          </label>
                          {newAgreement.file && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => setNewAgreement({ ...newAgreement, file: null })}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: THEME_COLOR }}>
                        إضافة الاتفاقية
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Agreements Table */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              {agreements.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <FileCheck size={48} className="mx-auto mb-4 opacity-50" />
                  <p>لا توجد اتفاقيات</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-4 text-center text-xs font-bold text-slate-500 uppercase w-12">الملاحق</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">رقم الاتفاقية</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">العنوان</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الطرف الثاني</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">التاريخ</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الملف</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الحالة</th>
                      {user?.role === 'legal_manager' && (
                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase">إجراءات</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {agreements.map((agreement) => (
                      <React.Fragment key={agreement.id}>
                        <tr className="hover:bg-slate-50 transition-colors">
                          {/* زر التوسيع لعرض الملاحق */}
                          <td className="px-3 py-4 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="p-1 h-8 w-8"
                              onClick={() => toggleAgreementExpand(agreement.id)}
                              data-testid={`expand-annexes-${agreement.id}`}
                            >
                              {expandedAgreements[agreement.id] ? (
                                <ChevronUp size={18} className="text-slate-500" />
                              ) : (
                                <ChevronDown size={18} className="text-slate-500" />
                              )}
                            </Button>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{agreement.agreement_number}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agreement.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{agreement.party_name}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {agreement.agreement_date ? new Date(agreement.agreement_date).toLocaleDateString('ar-SA') : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            {agreement.file_name ? (
                              <a
                                href={`/api/files/agreement/${agreement.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <FileText size={14} />
                                {agreement.file_name.length > 15 ? agreement.file_name.substring(0, 15) + '...' : agreement.file_name}
                              </a>
                            ) : (
                              <span className="text-slate-400">لا يوجد ملف</span>
                            )}
                          </td>
                          <td className="px-6 py-4">{getContractStatusBadge(agreement.status)}</td>
                          {user?.role === 'legal_manager' && (
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2 justify-center">
                                {/* زر إضافة ملحق */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                                  onClick={() => {
                                    setSelectedAgreementForAnnex(agreement.id);
                                    setIsAddAnnexDialogOpen(true);
                                  }}
                                  data-testid={`add-annex-btn-${agreement.id}`}
                                >
                                  <Paperclip size={14} className="ml-1" />
                                  ملحق
                                </Button>
                                {/* أزرار تغيير الحالة حسب الحالة الحالية */}
                                {agreement.status === 'draft' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                    onClick={() => handleAgreementStatusChange(agreement.id, 'review')}
                                  >
                                    إرسال للمراجعة
                                  </Button>
                                )}
                                {agreement.status === 'review' && (
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => handleAgreementStatusChange(agreement.id, 'approved')}
                                  >
                                    اعتماد
                                  </Button>
                                )}
                                {agreement.status === 'approved' && (
                                  <Button
                                    size="sm"
                                    className="text-white"
                                    style={{ backgroundColor: THEME_COLOR }}
                                    onClick={() => handleAgreementStatusChange(agreement.id, 'signed')}
                                  >
                                    تفعيل
                                  </Button>
                                )}
                                {/* زر تحديث/رفع الملف */}
                                <label className="cursor-pointer">
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    className="hidden"
                                    onChange={(e) => handleUpdateAgreementFile(agreement.id, e.target.files[0])}
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    asChild
                                  >
                                    <span>
                                      <Upload size={16} />
                                    </span>
                                  </Button>
                                </label>
                                {/* زر الحذف */}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => openDeleteDialog('agreement', agreement.id, agreement.title)}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                        {/* صف الملاحق (يظهر عند التوسيع) */}
                        {expandedAgreements[agreement.id] && (
                          <tr className="bg-purple-50/50">
                            <td colSpan={user?.role === 'legal_manager' ? 8 : 7} className="px-6 py-4">
                              <div className="mr-8 border-r-2 border-purple-200 pr-4">
                                <h4 className="text-sm font-bold text-purple-700 mb-3 flex items-center gap-2">
                                  <Paperclip size={16} />
                                  ملاحق الاتفاقية
                                </h4>
                                {annexes[agreement.id]?.length > 0 ? (
                                  <div className="space-y-2">
                                    {annexes[agreement.id].map((annex) => (
                                      <div key={annex.id} className="bg-white rounded-lg border border-purple-200 p-3 flex items-center justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-3">
                                            <span className="text-sm font-medium text-slate-900">{annex.annex_number}</span>
                                            <span className="text-sm text-slate-600">{annex.title}</span>
                                            {annex.annex_date && (
                                              <span className="text-xs text-slate-400">
                                                ({new Date(annex.annex_date).toLocaleDateString('ar-SA')})
                                              </span>
                                            )}
                                          </div>
                                          {annex.description && (
                                            <p className="text-xs text-slate-500 mt-1">{annex.description}</p>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {annex.file_name ? (
                                            <a
                                              href={`/api/files/annex/${annex.id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
                                            >
                                              <FileText size={14} />
                                              {annex.file_name.length > 12 ? annex.file_name.substring(0, 12) + '...' : annex.file_name}
                                            </a>
                                          ) : user?.role === 'legal_manager' ? (
                                            <label className="cursor-pointer">
                                              <input
                                                type="file"
                                                accept=".pdf"
                                                className="hidden"
                                                onChange={(e) => handleUploadAnnexFile(annex.id, e.target.files[0], agreement.id)}
                                              />
                                              <Button size="sm" variant="outline" className="text-xs h-7" asChild>
                                                <span>
                                                  <Upload size={12} className="ml-1" />
                                                  رفع ملف
                                                </span>
                                              </Button>
                                            </label>
                                          ) : (
                                            <span className="text-xs text-slate-400">لا يوجد ملف</span>
                                          )}
                                          {user?.role === 'legal_manager' && (
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                              onClick={() => handleDeleteAnnex(annex.id, agreement.id)}
                                            >
                                              <Trash2 size={14} />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-slate-500">لا توجد ملاحق لهذه الاتفاقية</p>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Commitments Tab Content - IFG Only */}
        {activeTab === 'commitments' && isIFGCompany(company?.name_ar) && (
          <>
            {/* Search, Filter and Add Button Bar for Commitments - All in One Row */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder="البحث في الإقرارات..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 h-11 bg-white border-slate-200"
                  data-testid="commitments-search-input"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="commitments-filter-status-select">
                  <Filter size={16} className="ml-2" />
                  <SelectValue placeholder="تصفية حسب الحالة" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="draft">مسودة</SelectItem>
                  <SelectItem value="review">قيد المراجعة</SelectItem>
                  <SelectItem value="approved">معتمد</SelectItem>
                  <SelectItem value="signed">موقّع</SelectItem>
                </SelectContent>
              </Select>
              {user?.role === 'legal_manager' && (
                <Dialog open={isAddCommitmentDialogOpen} onOpenChange={setIsAddCommitmentDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="text-white h-11 px-6 shadow-md"
                      style={{ backgroundColor: THEME_COLOR }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                      data-testid="add-commitment-button"
                    >
                      <Plus size={20} className="ml-2" />
                      إضافة إقرار جديد
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]" dir="rtl">
                    <DialogHeader>
                      <DialogTitle className="text-right text-2xl">إضافة إقرار جديد</DialogTitle>
                      <DialogDescription className="text-right">أدخل تفاصيل الإقرار</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateCommitment} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-right block">رقم الإقرار *</Label>
                          <Input
                            value={newCommitment.commitment_number}
                            onChange={(e) => setNewCommitment({ ...newCommitment, commitment_number: e.target.value })}
                            required
                            className="text-right"
                            placeholder="مثال: CMT-2024-001"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-right block">تاريخ الإقرار</Label>
                          <Input
                            type="date"
                            value={newCommitment.commitment_date}
                            onChange={(e) => setNewCommitment({ ...newCommitment, commitment_date: e.target.value })}
                            className="text-left"
                            dir="ltr"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-right block">اسم الموظف *</Label>
                          <Input
                            value={newCommitment.employee_name}
                            onChange={(e) => setNewCommitment({ ...newCommitment, employee_name: e.target.value })}
                            required
                            className="text-right"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-right block">رقم الموظف</Label>
                          <Input
                            value={newCommitment.employee_id}
                            onChange={(e) => setNewCommitment({ ...newCommitment, employee_id: e.target.value })}
                            className="text-right"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">نوع الإقرار *</Label>
                        <Input
                          value={newCommitment.commitment_type}
                          onChange={(e) => setNewCommitment({ ...newCommitment, commitment_type: e.target.value })}
                          required
                          className="text-right"
                          placeholder="مثال: إقرار بالحفاظ على السرية"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">الوصف</Label>
                        <Textarea
                          value={newCommitment.description}
                          onChange={(e) => setNewCommitment({ ...newCommitment, description: e.target.value })}
                          className="text-right"
                          rows={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">ملف الإقرار (PDF)</Label>
                        <div className="flex items-center gap-3">
                          <label className="flex-1 cursor-pointer">
                            <Input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => setNewCommitment({ ...newCommitment, file: e.target.files[0] })}
                              data-testid="commitment-file-input"
                            />
                            <div className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-blue-500 transition-colors">
                              <Upload size={18} className="text-slate-500" />
                              <span className="text-sm text-slate-600">
                                {newCommitment.file ? newCommitment.file.name : 'اختر ملف PDF'}
                              </span>
                            </div>
                          </label>
                          {newCommitment.file && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-700"
                              onClick={() => setNewCommitment({ ...newCommitment, file: null })}
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: THEME_COLOR }}>
                        إضافة الإقرار
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Commitments Table */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              {commitments.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>لا توجد إقرارات</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">رقم الإقرار</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">اسم الموظف</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">رقم الموظف</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">نوع الإقرار</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">التاريخ</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الملف</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الحالة</th>
                      {user?.role === 'legal_manager' && (
                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase">إجراءات</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {commitments.map((commitment) => (
                      <tr key={commitment.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{commitment.commitment_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{commitment.employee_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{commitment.employee_id || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{commitment.commitment_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {commitment.commitment_date ? new Date(commitment.commitment_date).toLocaleDateString('ar-SA') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {commitment.file_name ? (
                            <a
                              href={`/api/files/commitment/${commitment.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <FileText size={14} />
                              {commitment.file_name.length > 15 ? commitment.file_name.substring(0, 15) + '...' : commitment.file_name}
                            </a>
                          ) : (
                            <span className="text-slate-400">لا يوجد ملف</span>
                          )}
                        </td>
                        <td className="px-6 py-4">{getContractStatusBadge(commitment.status)}</td>
                        {user?.role === 'legal_manager' && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 justify-center">
                              {/* أزرار تغيير الحالة حسب الحالة الحالية */}
                              {commitment.status === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleCommitmentStatusChange(commitment.id, 'review')}
                                >
                                  إرسال للمراجعة
                                </Button>
                              )}
                              {commitment.status === 'review' && (
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => handleCommitmentStatusChange(commitment.id, 'approved')}
                                >
                                  اعتماد
                                </Button>
                              )}
                              {commitment.status === 'approved' && (
                                <Button
                                  size="sm"
                                  className="text-white"
                                  style={{ backgroundColor: THEME_COLOR }}
                                  onClick={() => handleCommitmentStatusChange(commitment.id, 'signed')}
                                >
                                  توقيع
                                </Button>
                              )}
                              {/* زر تحديث/رفع الملف */}
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept=".pdf"
                                  className="hidden"
                                  onChange={(e) => handleUpdateCommitmentFile(commitment.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  asChild
                                >
                                  <span>
                                    <Upload size={16} />
                                  </span>
                                </Button>
                              </label>
                              {/* زر الحذف */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openDeleteDialog('commitment', commitment.id, commitment.employee_name)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Letters Tab Content - IFG و أوتاد العمران */}
        {activeTab === 'letters' && hasLettersModule(company?.name_ar) && (
          <>
            {/* Search, Filter and Add Button Bar for Letters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder="البحث في الخطابات..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pr-10 h-11 bg-white border-slate-200"
                  data-testid="letters-search-input"
                />
              </div>
              <Select value={letterTypeFilter} onValueChange={setLetterTypeFilter}>
                <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="letters-filter-type-select">
                  <Filter size={16} className="ml-2" />
                  <SelectValue placeholder="تصفية حسب النوع" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الخطابات</SelectItem>
                  <SelectItem value="outgoing">صادر</SelectItem>
                  <SelectItem value="incoming">وارد</SelectItem>
                </SelectContent>
              </Select>
              {user?.role === 'legal_manager' && (
                <Dialog open={isAddLetterDialogOpen} onOpenChange={setIsAddLetterDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="text-white h-11 px-6 shadow-md"
                      style={{ backgroundColor: THEME_COLOR }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                      data-testid="add-letter-button"
                    >
                      <Plus size={20} className="ml-2" />
                      إضافة خطاب جديد
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]" dir="rtl">
                    <DialogHeader>
                      <DialogTitle className="text-right text-2xl">إضافة خطاب جديد</DialogTitle>
                      <DialogDescription className="text-right">أدخل تفاصيل الخطاب</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateLetter} className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-right block">رقم الخطاب *</Label>
                          <Input
                            value={newLetter.letter_number}
                            onChange={(e) => setNewLetter({ ...newLetter, letter_number: e.target.value })}
                            required
                            className="text-right"
                            placeholder="مثال: LTR-2024-001"
                            data-testid="letter-number-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-right block">تاريخ الخطاب</Label>
                          <Input
                            type="date"
                            value={newLetter.letter_date}
                            onChange={(e) => setNewLetter({ ...newLetter, letter_date: e.target.value })}
                            className="text-left"
                            dir="ltr"
                            data-testid="letter-date-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">نوع الخطاب *</Label>
                        <Select 
                          value={newLetter.letter_type} 
                          onValueChange={(value) => setNewLetter({ ...newLetter, letter_type: value })}
                        >
                          <SelectTrigger className="w-full" data-testid="letter-type-select">
                            <SelectValue placeholder="اختر نوع الخطاب" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="outgoing">صادر</SelectItem>
                            <SelectItem value="incoming">وارد</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">موضوع الخطاب *</Label>
                        <Input
                          value={newLetter.subject}
                          onChange={(e) => setNewLetter({ ...newLetter, subject: e.target.value })}
                          required
                          className="text-right"
                          data-testid="letter-subject-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-right block">المرسل</Label>
                          <Input
                            value={newLetter.sender}
                            onChange={(e) => setNewLetter({ ...newLetter, sender: e.target.value })}
                            className="text-right"
                            data-testid="letter-sender-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-right block">المرسل إليه</Label>
                          <Input
                            value={newLetter.recipient}
                            onChange={(e) => setNewLetter({ ...newLetter, recipient: e.target.value })}
                            className="text-right"
                            data-testid="letter-recipient-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">ملاحظات</Label>
                        <Textarea
                          value={newLetter.description}
                          onChange={(e) => setNewLetter({ ...newLetter, description: e.target.value })}
                          className="text-right"
                          rows={2}
                          data-testid="letter-description-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-right block">ملف PDF</Label>
                        <Input
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setNewLetter({ ...newLetter, file: e.target.files[0] })}
                          className="text-right"
                          data-testid="letter-file-input"
                        />
                      </div>
                      <Button type="submit" className="w-full" style={{ backgroundColor: THEME_COLOR }} data-testid="submit-letter-btn">
                        إضافة الخطاب
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>

            {/* Letters Table */}
            <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
              {letters.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  <Mail size={48} className="mx-auto mb-4 opacity-50" />
                  <p>لا توجد خطابات</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">رقم الخطاب</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الموضوع</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">النوع</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">المرسل</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">المرسل إليه</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">التاريخ</th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase">الملف</th>
                      {user?.role === 'legal_manager' && (
                        <th className="px-6 py-4 text-center text-xs font-bold text-slate-500 uppercase">إجراءات</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {letters.map((letter) => (
                      <tr key={letter.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{letter.letter_number}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.subject}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            letter.letter_type === 'outgoing' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {letter.letter_type === 'outgoing' ? 'صادر' : 'وارد'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.sender || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{letter.recipient || '-'}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {letter.letter_date ? new Date(letter.letter_date).toLocaleDateString('ar-SA') : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {letter.file_name ? (
                            <a
                              href={`/api/files/letter/${letter.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <FileText size={14} />
                              {letter.file_name.length > 15 ? letter.file_name.substring(0, 15) + '...' : letter.file_name}
                            </a>
                          ) : (
                            <span className="text-slate-400">لا يوجد ملف</span>
                          )}
                        </td>
                        {user?.role === 'legal_manager' && (
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 justify-center">
                              {/* زر تحديث/رفع الملف */}
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept=".pdf"
                                  className="hidden"
                                  onChange={(e) => handleUploadLetterFile(letter.id, e.target.files[0])}
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  asChild
                                >
                                  <span>
                                    <Upload size={16} />
                                  </span>
                                </Button>
                              </label>
                              {/* زر الحذف */}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => openDeleteDialog('letter', letter.id, letter.subject)}
                                data-testid={`delete-letter-${letter.id}`}
                              >
                                <Trash2 size={16} />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Contracts/Cases Actions Bar */}
        {(activeTab === 'contracts' || activeTab === 'cases') && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                placeholder={`البحث في ${activeTab === 'contracts' ? 'العقود' : 'القضايا'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10 h-11 bg-white border-slate-200"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 h-11 bg-white border-slate-200" data-testid="filter-status-select">
                <Filter size={16} className="ml-2" />
                <SelectValue placeholder="تصفية حسب الحالة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                {activeTab === 'contracts' ? (
                  <>
                    <SelectItem value="draft">مسودة</SelectItem>
                    <SelectItem value="review">قيد المراجعة</SelectItem>
                    <SelectItem value="approved">معتمد</SelectItem>
                    <SelectItem value="signed">موقّع</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="new">جديدة</SelectItem>
                    <SelectItem value="filed">مرفوعة</SelectItem>
                    <SelectItem value="hearings">جلسات</SelectItem>
                    <SelectItem value="final_judgment">حكم نهائي</SelectItem>
                    <SelectItem value="execution">تنفيذ</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            {activeTab === 'contracts' ? (
              <Dialog open={isAddContractDialogOpen} onOpenChange={setIsAddContractDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="text-white h-11 px-6 shadow-md"
                    style={{ backgroundColor: THEME_COLOR }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                    data-testid="add-contract-button"
                  >
                    <Plus size={20} className="ml-2" />
                    إضافة عقد جديد
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" dir="rtl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-right">إضافة عقد جديد</DialogTitle>
                    <DialogDescription className="text-right">
                      أدخل تفاصيل العقد الجديد لـ {company.name_ar}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateContract} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
                    {/* Project Selection for Awtad */}
                    {showProjects && projects.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="contract-project" className="text-right block">اسم المشروع</Label>
                        <Select
                          value={newContract.project_id || "none"}
                          onValueChange={(value) => setNewContract({ ...newContract, project_id: value === "none" ? "" : value })}
                        >
                          <SelectTrigger className="w-full text-right" data-testid="contract-project-select">
                            <SelectValue placeholder="اختر المشروع (اختياري)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">بدون مشروع</SelectItem>
                            {projects.map((project) => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="contract-title" className="text-right block">عنوان العقد *</Label>
                      <Input
                        id="contract-title"
                        value={newContract.title}
                        onChange={(e) => setNewContract({ ...newContract, title: e.target.value })}
                        required
                        className="text-right"
                        data-testid="contract-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contract-description" className="text-right block">الوصف *</Label>
                      <Textarea
                        id="contract-description"
                        value={newContract.description}
                        onChange={(e) => setNewContract({ ...newContract, description: e.target.value })}
                        required
                        rows={3}
                        className="text-right"
                        data-testid="contract-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contract-number" className="text-right block">رقم العقد *</Label>
                        <Input
                          id="contract-number"
                          value={newContract.contract_number}
                          onChange={(e) => setNewContract({ ...newContract, contract_number: e.target.value })}
                          required
                          className="text-right"
                          placeholder="مثال: 2024/001"
                          data-testid="contract-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contract-party" className="text-right block">الطرف الثاني *</Label>
                        <Input
                          id="contract-party"
                          value={newContract.party_name}
                          onChange={(e) => setNewContract({ ...newContract, party_name: e.target.value })}
                          required
                          className="text-right"
                          data-testid="contract-party-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contract-value" className="text-right block">قيمة العقد (اختياري)</Label>
                      <Input
                        id="contract-value"
                        type="number"
                        step="0.01"
                        value={newContract.contract_value}
                        onChange={(e) => setNewContract({ ...newContract, contract_value: e.target.value })}
                        className="text-right"
                        data-testid="contract-value-input"
                      />
                      <p className="text-xs text-slate-400">* لا يشمل ضريبة القيمة المضافة</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="contract-notes" className="text-right block">ملاحظات (اختياري)</Label>
                      <Textarea
                        id="contract-notes"
                        value={newContract.notes}
                        onChange={(e) => setNewContract({ ...newContract, notes: e.target.value })}
                        rows={2}
                        className="text-right"
                        data-testid="contract-notes-input"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1 text-white" style={{ backgroundColor: THEME_COLOR }} data-testid="submit-contract-button">
                        إضافة العقد
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddContractDialogOpen(false)}
                        className="flex-1"
                        data-testid="cancel-contract-button"
                      >
                        إلغاء
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            ) : (
              <Dialog open={isAddCaseDialogOpen} onOpenChange={setIsAddCaseDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    className="text-white h-11 px-6 shadow-md"
                    style={{ backgroundColor: THEME_COLOR }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR_HOVER}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = THEME_COLOR}
                    data-testid="add-case-button"
                  >
                    <Plus size={20} className="ml-2" />
                    إضافة قضية جديدة
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl" dir="rtl">
                  <DialogHeader>
                    <DialogTitle className="text-2xl text-right">إضافة قضية جديدة</DialogTitle>
                    <DialogDescription className="text-right">
                      أدخل تفاصيل القضية الجديدة لـ {company.name_ar}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateCase} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="case-number" className="text-right block">رقم القضية *</Label>
                        <Input
                          id="case-number"
                          value={newCase.case_number}
                          onChange={(e) => setNewCase({ ...newCase, case_number: e.target.value })}
                          required
                          className="text-right"
                          data-testid="case-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="case-type" className="text-right block">نوع القضية *</Label>
                        <Input
                          id="case-type"
                          value={newCase.case_type}
                          onChange={(e) => setNewCase({ ...newCase, case_type: e.target.value })}
                          required
                          className="text-right"
                          data-testid="case-type-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-title" className="text-right block">عنوان القضية *</Label>
                      <Input
                        id="case-title"
                        value={newCase.title}
                        onChange={(e) => setNewCase({ ...newCase, title: e.target.value })}
                        required
                        className="text-right"
                        data-testid="case-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-description" className="text-right block">الوصف *</Label>
                      <Textarea
                        id="case-description"
                        value={newCase.description}
                        onChange={(e) => setNewCase({ ...newCase, description: e.target.value })}
                        required
                        rows={3}
                        className="text-right"
                        data-testid="case-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="case-plaintiff" className="text-right block">المدعي *</Label>
                        <Input
                          id="case-plaintiff"
                          value={newCase.plaintiff}
                          onChange={(e) => setNewCase({ ...newCase, plaintiff: e.target.value })}
                          required
                          className="text-right"
                          data-testid="case-plaintiff-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="case-defendant" className="text-right block">المدعى عليه *</Label>
                        <Input
                          id="case-defendant"
                          value={newCase.defendant}
                          onChange={(e) => setNewCase({ ...newCase, defendant: e.target.value })}
                          required
                          className="text-right"
                          data-testid="case-defendant-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-court" className="text-right block">اسم المحكمة (اختياري)</Label>
                      <Input
                        id="case-court"
                        value={newCase.court_name}
                        onChange={(e) => setNewCase({ ...newCase, court_name: e.target.value })}
                        className="text-right"
                        data-testid="case-court-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="case-notes" className="text-right block">ملاحظات (اختياري)</Label>
                      <Textarea
                        id="case-notes"
                        value={newCase.notes}
                        onChange={(e) => setNewCase({ ...newCase, notes: e.target.value })}
                        rows={2}
                        className="text-right"
                        data-testid="case-notes-input"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button type="submit" className="flex-1 text-white" style={{ backgroundColor: THEME_COLOR }} data-testid="submit-case-button">
                        إضافة القضية
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsAddCaseDialogOpen(false)}
                        className="flex-1"
                        data-testid="cancel-case-button"
                      >
                        إلغاء
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* Contracts/Cases Data List */}
        {(activeTab === 'contracts' || activeTab === 'cases') && (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="spinner" />
              </div>
            ) : activeTab === 'contracts' ? (
              contracts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText size={48} className="mb-4" />
                  <p className="text-lg">لا توجد عقود لهذه الشركة</p>
                  <p className="text-sm mt-2">أضف عقداً جديداً للبدء</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان العقد</th>
                        {showProjects && <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المشروع</th>}
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم العقد</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الطرف الثاني</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {contracts.map((contract) => {
                        const projectName = projects.find(p => p.id === contract.project_id)?.name;
                        return (
                          <tr
                            key={contract.id}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/contract/${contract.id}`)}
                            data-testid={`contract-row-${contract.id}`}
                          >
                            <td className="px-6 py-4 text-sm font-medium text-slate-900">{contract.title}</td>
                            {showProjects && <td className="px-6 py-4 text-sm text-slate-600">{projectName || '-'}</td>}
                            <td className="px-6 py-4 text-sm text-slate-600">{contract.contract_number || contract.contract_type || '-'}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">{contract.party_name}</td>
                            <td className="px-6 py-4">{getContractStatusBadge(contract.status)}</td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {new Date(contract.created_at).toLocaleDateString('ar-SA')}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-2">
                                {/* زر الاستعراض */}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/contract/${contract.id}`);
                                  }}
                                  data-testid={`view-contract-${contract.id}`}
                                >
                                  استعراض
                                </Button>
                                {/* أزرار الإجراءات حسب الحالة - للمدير القانوني فقط */}
                                {user?.role === 'legal_manager' && (
                                  <>
                                    {contract.status === 'draft' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStatusChange(contract.id, 'review', 'contract');
                                        }}
                                        data-testid={`send-to-review-${contract.id}`}
                                      >
                                        إرسال للمراجعة
                                      </Button>
                                    )}
                                    {contract.status === 'review' && (
                                      <Button
                                        size="sm"
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleApproveContract(contract.id);
                                        }}
                                        data-testid={`approve-contract-${contract.id}`}
                                      >
                                        اعتماد
                                      </Button>
                                    )}
                                    {contract.status === 'approved' && (
                                      <Button
                                        size="sm"
                                        className="text-white"
                                        style={{ backgroundColor: THEME_COLOR }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/contract/${contract.id}`);
                                        }}
                                        data-testid={`sign-contract-${contract.id}`}
                                      >
                                        توقيع
                                      </Button>
                                    )}
                                    {/* زر تحديث/رفع الملف */}
                                    <label className="cursor-pointer" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="file"
                                        accept=".pdf"
                                        className="hidden"
                                        onChange={(e) => {
                                          handleUpdateContractFile(contract.id, e.target.files[0]);
                                        }}
                                      />
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        asChild
                                      >
                                        <span>
                                          <Upload size={16} />
                                        </span>
                                      </Button>
                                    </label>
                                    {/* زر الحذف - للمدير القانوني فقط */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteDialog('contract', contract.id, contract.title);
                                      }}
                                      data-testid={`delete-contract-${contract.id}`}
                                    >
                                      <Trash2 size={16} />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              cases.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <Scale size={48} className="mb-4" />
                  <p className="text-lg">لا توجد قضايا لهذه الشركة</p>
                  <p className="text-sm mt-2">أضف قضية جديدة للبدء</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">رقم القضية</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">عنوان القضية</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">نوع القضية</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">المدعي</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">الحالة</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">تاريخ الإنشاء</th>
                        <th className="px-6 py-4 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">إجراءات</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {cases.map((caseItem) => (
                        <tr
                          key={caseItem.id}
                          className="hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/case/${caseItem.id}`)}
                          data-testid={`case-row-${caseItem.id}`}
                        >
                          <td className="px-6 py-4 text-sm font-medium text-slate-900">{caseItem.case_number}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{caseItem.title}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{caseItem.case_type}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">{caseItem.plaintiff}</td>
                          <td className="px-6 py-4">{getCaseStatusBadge(caseItem.status)}</td>
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {new Date(caseItem.created_at).toLocaleDateString('ar-SA')}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {/* زر الاستعراض */}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/case/${caseItem.id}`);
                                }}
                                data-testid={`view-case-${caseItem.id}`}
                              >
                                استعراض
                              </Button>
                              {/* زر الحذف - للمدير القانوني فقط */}
                              {user?.role === 'legal_manager' && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteDialog('case', caseItem.id, caseItem.title);
                                  }}
                                  data-testid={`delete-case-${caseItem.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}

      {/* Add Annex Dialog */}
      <Dialog open={isAddAnnexDialogOpen} onOpenChange={setIsAddAnnexDialogOpen}>
        <DialogContent className="sm:max-w-[450px]" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right text-2xl flex items-center gap-2">
              <Paperclip size={24} className="text-purple-600" />
              إضافة ملحق جديد
            </DialogTitle>
            <DialogDescription className="text-right">أدخل تفاصيل الملحق للاتفاقية</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateAnnex} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-right block">رقم الملحق *</Label>
                <Input
                  value={newAnnex.annex_number}
                  onChange={(e) => setNewAnnex({ ...newAnnex, annex_number: e.target.value })}
                  required
                  className="text-right"
                  placeholder="مثال: ملحق 1"
                  data-testid="annex-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-right block">تاريخ الملحق</Label>
                <Input
                  type="date"
                  value={newAnnex.annex_date}
                  onChange={(e) => setNewAnnex({ ...newAnnex, annex_date: e.target.value })}
                  className="text-left"
                  dir="ltr"
                  data-testid="annex-date-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-right block">عنوان الملحق *</Label>
              <Input
                value={newAnnex.title}
                onChange={(e) => setNewAnnex({ ...newAnnex, title: e.target.value })}
                required
                className="text-right"
                data-testid="annex-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">الوصف</Label>
              <Textarea
                value={newAnnex.description}
                onChange={(e) => setNewAnnex({ ...newAnnex, description: e.target.value })}
                className="text-right"
                rows={2}
                data-testid="annex-description-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">ملف PDF</Label>
              <Input
                type="file"
                accept=".pdf"
                onChange={(e) => setNewAnnex({ ...newAnnex, file: e.target.files[0] })}
                className="text-right"
                data-testid="annex-file-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full text-white" 
              style={{ backgroundColor: '#9333ea' }}
              data-testid="submit-annex-btn"
            >
              <Paperclip size={16} className="ml-2" />
              إضافة الملحق
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Notifications Dialog */}
      <Dialog open={isNotificationsOpen} onOpenChange={setIsNotificationsOpen}>
        <DialogContent className="max-w-2xl" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-2xl text-right">الإشعارات</DialogTitle>
            <DialogDescription className="text-right">
              آخر التحديثات والإشعارات
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-3 mt-4">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Bell size={48} className="mx-auto mb-4 opacity-50" />
                <p>لا توجد إشعارات</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    notification.read
                      ? 'bg-slate-50 border-slate-200'
                      : 'bg-pink-50 border-pink-200'
                  }`}
                  onClick={() => {
                    markNotificationAsRead(notification.id);
                    if (notification.related_type === 'contract' && notification.related_id) {
                      navigate(`/contract/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    } else if (notification.related_type === 'case' && notification.related_id) {
                      navigate(`/case/${notification.related_id}`);
                      setIsNotificationsOpen(false);
                    }
                  }}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-slate-900 mb-1">{notification.title}</h4>
                      <p className="text-sm text-slate-600">{notification.message}</p>
                      <p className="text-xs text-slate-400 mt-2">
                        {new Date(notification.created_at).toLocaleString('ar-SA')}
                      </p>
                    </div>
                    {!notification.read && (
                      <div className="w-2 h-2 rounded-full mt-2" style={{ backgroundColor: THEME_COLOR }} />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-xl text-right flex items-center gap-2 text-red-600">
              <AlertCircle size={24} />
              تأكيد الحذف
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-800 font-medium text-center mb-2">
                ⚠️ تحذير: هذا الإجراء لا يمكن التراجع عنه
              </p>
              <p className="text-red-700 text-sm text-center">
                سيتم حذف {getDeleteTypeLabel(deleteTarget.type)} &quot;{deleteTarget.title}&quot; نهائياً من النظام
              </p>
            </div>
            <p className="text-slate-600 text-center text-sm">
              هل أنت متأكد من رغبتك في الحذف؟
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setDeleteTarget({ type: '', id: '', title: '' });
              }}
            >
              إلغاء
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmDelete}
              data-testid="confirm-delete-btn"
            >
              <Trash2 size={16} className="ml-2" />
              نعم، احذف نهائياً
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      </main>
      </div>
    </div>
  );
}
